import { currentEnvironmentName } from '../../../application/configurations/envConfig';
import RefinerJson from '../../../application/configurations/refiner.json';
import { sha256 } from 'js-sha256';
import { UtilityService } from '../utils';

function getEyApp(url: string = window.location.href): string {
  return url.includes('c=intranet') ? 'Intranet Search' : 'Discover Search';
}

/**
 * Ensures the Adobe data layer exists on the window object.
 */
export function ensureAdobeDataLayer() {
  if (!(window as any).adobeDataLayer) {
    (window as any).adobeDataLayer = [];
  }
}

/**
 * Builds refinementType (display names) and refinementValue (leaf values) arrays
 * from a refiner store array (e.g. store.selectedRefiners) for a given vertical.
 * Shared by Clear All handlers in container components.
 * `labelMap` (browse: the vertical's browseConfig `filters` object) takes priority so
 * the reported names match the labels actually rendered in the UI.
 */
export function buildRefinementArrays(
  refinerStore: Record<string, string[]>[],
  vertical: string,
  labelMap?: Record<string, string>
): { refinementType: string[]; refinementValue: string[] } {
  const refinementType: string[] = [];
  const refinementValue: string[] = [];
  const verticalMap: Record<string, string | undefined> =
    labelMap ??
    (RefinerJson as any[]).find(
      (item) => item.vertical.toLowerCase() === vertical.toLowerCase()
    ) ??
    {};
  refinerStore.forEach((refinerObj) => {
    const key = Object.keys(refinerObj)[0];
    const values = Object.values(refinerObj)[0];
    if (key && values && values.length > 0) {
      refinementType.push(verticalMap[key] ?? key);
      values.forEach((val) => {
        refinementValue.push(val.includes('\\') ? val.split('\\').pop()! : val);
      });
    }
  });

  return { refinementType, refinementValue };
}

/**
 * Injects the Adobe Launch script into the document head based on environment.
 * Environments local/development/qa use the development script,
 * ua/prodstg use the staging script, and prod uses the production script.
 */
export function injectAdobeTagScript() {
  if (document.getElementById('adobe-launch-script')) return;

  let src = '';
  switch (currentEnvironmentName) {
    case 'local':
    case 'development':
    case 'qa':
      src =
        'https://assets.adobedtm.com/68ca638811bd/6503853cb88c/launch-a32e910d6ceb-development.min.js';
      break;
    case 'ua':
      src =
        'https://assets.adobedtm.com/68ca638811bd/6503853cb88c/launch-08d54256368e-staging.min.js';
      break;
    case 'prod':
    case 'prodstg':
      src =
        'https://assets.adobedtm.com/68ca638811bd/6503853cb88c/launch-5ca050f7020e.min.js';
      break;
    default:
      return;
  }

  const script = document.createElement('script');
  script.id = 'adobe-launch-script';
  script.src = src;
  script.async = true;
  document.head.appendChild(script);
}

/**
 * Hashes an email address using SHA-256.
 * @param email The email address to hash.
 * @returns A hex-encoded SHA-256 hash of the lowercased email.
 */
export async function hashEmailSHA256(email: string): Promise<string> {
  const lowerCaseEmail = email.toLowerCase();
  const encoder = new TextEncoder();
  const data = encoder.encode(lowerCaseEmail);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Pushes a page view event to the Adobe data layer.
 *
 * @param pageName            The name of the current page.
 * @param isErrorPage         Whether the page is an error page.
 * @param isHomePage          Whether the page is the home page.
 * @param siteSection         The site section the page belongs to.
 * @param url                 The full URL of the current page.
 * @param hashedEmailAddress  SHA-256 hashed email address of the user.
 * @param pageType            The type/category of the page.
 * @param country             [optional] Country of the user.
 * @param language            [optional] Language of the page/user.
 */
export function pushPageViewEvent(
  pageName: string,
  isErrorPage: boolean,
  isHomePage: boolean,
  siteSection: string,
  url: string,
  hashedEmailAddress: string,
  pageType: string,
  country?: string,
  language?: string
) {
  ensureAdobeDataLayer();

  const previousPageName = sessionStorage.getItem('previousPageName') ?? '';
  const originalReferrer = sessionStorage.getItem('originalReferrer') ?? '';

  const obj = {
    event: 'pageView',
    web: {
      webPageDetails: {
        name: pageName,
        isErrorPage: isErrorPage,
        isHomePage: isHomePage,
        siteSection: siteSection,
        URL: url
      }
    },
    _etassureco: {
      identityDetails: {
        hashedEmailAddress: hashedEmailAddress
      },
      eyApp: getEyApp(url),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined',
      page: {
        previousPageName: previousPageName,
        originalReferrer: originalReferrer,
        pageType: pageType,
        customPageViews: {
          value: 1
        },
        ...(country && { country: country }),
        ...(language && { language: language })
      }
    }
  };

  // Update session storage for next page navigation
  sessionStorage.setItem('previousPageName', pageName);
  sessionStorage.setItem('originalReferrer', pageName.trim() !== '' ? url : '');

  console.log('Adobe page view event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a search query click-action event to the Adobe data layer.
 *
 * @param name            'Search' for direct input, 'Suggested Search' for suggestion popup, 'Recent Search' for recent search popup,
 *                        or 'Related searches:<term>' for related search chip clicks.
 * @param term            The keyword entered or clicked by the visitor.
 * @param numberOfResults Total number of results returned by the search.
 * @param options         Optional overrides: controlType (default 'Icon') and searchType (for related searches).
 */
export function pushSearchQueryEvent(
  name: string,
  term: string,
  numberOfResults: number,
  options?: {
    controlType?: 'Icon' | 'button';
    searchType?: string;
  }
) {
  ensureAdobeDataLayer();

  const controlType = options?.controlType ?? 'Icon';

  const supportSiteSearch: Record<string, any> = {
    locationInPage: 'body',
    term: term,
    instances: 1,
    nullInstances: numberOfResults === 0 ? 1 : 0,
    numberOfResults: numberOfResults
  };

  if (options?.searchType !== undefined) {
    supportSiteSearch.searchType = options.searchType;
  }

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: name,
        type: 'other'
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: controlType,
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch
    }
  };

  console.log('Adobe search query event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a Category (vertical) tab click-action event to the Adobe data layer.
 * Called after the search triggered by the tab click has returned results.
 *
 * @param name            The label of the Category tab clicked (e.g. "All", "Documents").
 * @param term            The current search keyword.
 * @param numberOfResults Total number of results returned for the selected tab.
 * @param url             [optional] Destination URL associated with the selected tab.
 */
export function pushTabClickEvent(
  name: string,
  term: string,
  numberOfResults: number,
  url?: string
) {
  ensureAdobeDataLayer();

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: name,
        type: 'other',
        ...(url && { URL: url })
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'tab',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        numberOfResults: numberOfResults
      }
    }
  };

  console.log('Adobe tab click event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a Sort By or Content Age drop-down selection event to the Adobe data layer.
 * Refinement type and value are included only when the search has active refiners.
 */
export function pushSortOrContentAgeEvent(
  name: string,
  term: string,
  numberOfResults: number,
  searchType: string,
  options?: {
    url?: string;
    refinementType?: string[];
    refinementValue?: string[];
  }
) {
  ensureAdobeDataLayer();

  const hasRefinements =
    (options?.refinementType?.length ?? 0) > 0 &&
    (options?.refinementValue?.length ?? 0) > 0;

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: name,
        type: 'other',
        ...(options?.url && { URL: options.url })
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'drop-down',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        numberOfResults: numberOfResults,
        searchType: searchType,
        ...(hasRefinements && {
          refinementType: options!.refinementType,
          refinementValue: options!.refinementValue
        })
      }
    }
  };

  console.log('Adobe sort or content age event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a Tile View / List View toggle click-action event to the Adobe data layer.
 *
 * @param name            'Tile View' or 'List View'.
 * @param term            The current search keyword.
 * @param numberOfResults Total number of results currently displayed.
 * @param searchType      The name of the active vertical (e.g. "All", "Documents").
 * @param options         Optional overrides: url, refinementType and refinementValue
 *                        (included only when the search has active refiners).
 */
export function pushTileOrListViewEvent(
  name: string,
  term: string,
  numberOfResults: number,
  searchType: string,
  options?: {
    url?: string;
    refinementType?: string[];
    refinementValue?: string[];
  }
) {
  ensureAdobeDataLayer();

  const hasRefinements =
    (options?.refinementType?.length ?? 0) > 0 &&
    (options?.refinementValue?.length ?? 0) > 0;

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: name,
        type: 'other',
        URL: options?.url ?? window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'button',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        nullInstances: numberOfResults === 0 ? 1 : 0,
        numberOfResults: numberOfResults,
        searchType: searchType,
        refinementInstances: 1,
        ...(hasRefinements && {
          refinementType: options!.refinementType,
          refinementValue: options!.refinementValue
        })
      }
    }
  };

  console.log('Adobe view toggle event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Extracts the content details of a result card that are shared by the
 * card title, Preview and View analytics events.
 */
function getResultContentDetails(item: any) {
  const isPeople = item.vertical?.toLowerCase() === 'people';
  const author =
    item.authorEmail
      ?.split(';')
      .map((email: string) => email.trim().toLowerCase())
      .filter((email: string) => email !== '')
      .map((email: string) => sha256(email))
      .join(',') ?? '';

  return {
    isPeople,
    title: UtilityService.removeHtmlTags(isPeople ? item.name ?? '' : item.title ?? ''),
    contentUrl: isPeople ? item.detailPath ?? '' : item.url ?? '',
    contentId: isPeople ? item.accountId ?? '' : item.ESC_EYAEMID ?? '',
    author,
    createdDate: item.major_public_date ?? ''
  };
}

/**
 * Pushes a result card title click event for all search verticals.
 */
export function pushCardTitleClickEvent(
  item: any,
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[]
) {
  ensureAdobeDataLayer();

  const { isPeople, title, contentUrl, contentId, author, createdDate } =
    getResultContentDetails(item);
  const populatedRefinementTypes = refinementType.filter((value) => value.trim() !== '');
  const populatedRefinementValues = refinementValue.filter((value) => value.trim() !== '');
  const hasRefinements =
    populatedRefinementTypes.length > 0 && populatedRefinementValues.length > 0;
  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: title,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'Cards',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined',
      search: {
        resultClicks: {
          value: 1
        }
      },
      content: {
        ...(contentId && { id: contentId }),
        ...(title && { title: title }),
        ...(!isPeople && author && { author: author }),
        ...(createdDate && { createdDate: createdDate }),
        url: contentUrl,
        views: {
          value: 1
        }
      }
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        numberOfResults: numberOfResults,
        searchType: searchType,
        ...(hasRefinements && {
          refinementType: populatedRefinementTypes,
          refinementValue: populatedRefinementValues
        })
      }
    }
  };

  console.log('Adobe card title click event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a Preview or View action click event for a result card.
 * Shared by both actions across all search verticals.
 *
 * @param action            'Preview' or 'View'.
 * @param item              The result card object that was acted upon.
 * @param term              The current search keyword.
 * @param numberOfResults   Total number of results returned with the active filters.
 * @param searchType        The name of the active vertical (e.g. "All", "Documents").
 * @param refinementType    Array of active filter category display names.
 * @param refinementValue   Array of active filter values.
 */
export function pushPreviewOrViewClickEvent(
  action: string,
  item: any,
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[]
) {
  ensureAdobeDataLayer();

  const { isPeople, title, contentUrl, contentId, author, createdDate } =
    getResultContentDetails(item);
  const populatedRefinementTypes = refinementType.filter((value) => value.trim() !== '');
  const populatedRefinementValues = refinementValue.filter((value) => value.trim() !== '');
  const hasRefinements =
    populatedRefinementTypes.length > 0 && populatedRefinementValues.length > 0;

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: `${action}: ${title}`,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'button',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined',
      search: {
        resultClicks: {
          value: 1
        }
      },
      content: {
        ...(contentId && { id: contentId }),
        ...(title && { title: title }),
        ...(!isPeople && author && { author: author }),
        ...(createdDate && { createdDate: createdDate }),
        url: contentUrl,
        views: {
          value: 1
        }
      }
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        numberOfResults: numberOfResults,
        searchType: searchType,
        ...(hasRefinements && {
          refinementType: populatedRefinementTypes,
          refinementValue: populatedRefinementValues
        })
      }
    }
  };

  console.log('Adobe preview or view click event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a "Share link" ellipsis-menu click event for a result card.
 *
 * @param item              The result card object whose link was shared.
 * @param term              The current search keyword.
 * @param numberOfResults   Total number of results returned with the active filters.
 * @param searchType        The name of the active vertical (e.g. "All", "Documents").
 * @param refinementType    Array of active filter category display names.
 * @param refinementValue   Array of active filter values.
 * @param sharedUrl         [optional] The URL actually copied to the clipboard.
 */
export function pushShareLinkClickEvent(
  item: any,
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[],
  sharedUrl?: string
) {
  ensureAdobeDataLayer();

  const { isPeople, title, contentUrl, contentId, author, createdDate } =
    getResultContentDetails(item);
  const url = sharedUrl || item.eyAlternativeUrl || item.detailPath || contentUrl;
  const populatedRefinementTypes = refinementType.filter((value) => value.trim() !== '');
  const populatedRefinementValues = refinementValue.filter((value) => value.trim() !== '');
  const hasRefinements =
    populatedRefinementTypes.length > 0 && populatedRefinementValues.length > 0;

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: `${title}: Share link Elipsis`,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'link',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined',
      search: {
        resultClicks: {
          value: 1
        }
      },
      content: {
        ...(contentId && { id: contentId }),
        ...(title && { title: title }),
        ...(!isPeople && author && { author: author }),
        ...(createdDate && { createdDate: createdDate }),
        ...(url && { url: url }),
        views: {
          value: 1
        }
      }
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        nullInstances: numberOfResults === 0 ? 1 : 0,
        numberOfResults: numberOfResults,
        searchType: searchType,
        refinementInstances: 1,
        ...(hasRefinements && {
          refinementType: populatedRefinementTypes,
          refinementValue: populatedRefinementValues
        })
      }
    }
  };

  console.log('Adobe share link click event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a "Download" ellipsis-menu click event for a result card.
 */
export function pushDownloadClickEvent(
  item: any,
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[],
  downloadUrl: string
) {
  ensureAdobeDataLayer();

  const { isPeople, title, contentUrl, contentId, author, createdDate } =
    getResultContentDetails(item);
  const attachment = Array.isArray(item.attachment)
    ? item.attachment[0]
    : item.attachment;
  const attachmentName = attachment
    ? attachment.split(/[\\/]/).pop() ?? ''
    : '';
  const extensionIndex = attachmentName.lastIndexOf('.');
  const fileName =
    extensionIndex > 0 ? attachmentName.slice(0, extensionIndex) : attachmentName;
  const fileFormat =
    extensionIndex > 0 ? attachmentName.slice(extensionIndex + 1) : '';
  const populatedRefinementTypes = refinementType.filter((value) => value.trim() !== '');
  const populatedRefinementValues = refinementValue.filter((value) => value.trim() !== '');
  const hasRefinements =
    populatedRefinementTypes.length > 0 && populatedRefinementValues.length > 0;
  const content = {
    ...(contentId && { id: contentId }),
    ...(title && { title: title }),
    ...(!isPeople && author && { author: author }),
    ...(createdDate && { createdDate: createdDate }),
    ...(downloadUrl && { url: downloadUrl }),
    ...(fileName && { fileName: fileName }),
    ...(fileFormat && { fileFormat: fileFormat }),
    downloads: { value: 1 },
    views: { value: 1 }
  };

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: `${title}: Download Elipsis`,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'link',
        linkClicks: { value: 1 }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined',
      search: {
        resultClicks: { value: 1 }
      },
      content
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        ...(term && { term: term }),
        instances: 1,
        nullInstances: numberOfResults === 0 ? 1 : 0,
        numberOfResults: numberOfResults,
        ...(searchType && { searchType: searchType }),
        refinementInstances: 1,
        ...(hasRefinements && {
          refinementType: populatedRefinementTypes,
          refinementValue: populatedRefinementValues
        })
      }
    }
  };

  console.log('Adobe download click event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a Previous / Next pagination click-action event to the Adobe data layer.
 */
export function pushPaginationEvent(
  action: 'Previous Page' | 'Next Page',
  currentPage: number,
  totalPages: number,
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[]
) {
  ensureAdobeDataLayer();

  const hasRefinements = refinementType.length > 0 && refinementValue.length > 0;

  const pageLabel =
    currentPage === 1
      ? 'First Page'
      : currentPage === totalPages
      ? 'Last Page'
      : action;

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: `${pageLabel}:${currentPage}/${totalPages}`,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'Pagination',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        nullInstances: numberOfResults === 0 ? 1 : 0,
        numberOfResults: numberOfResults,
        searchType: searchType,
        refinementInstances: 1,
        ...(hasRefinements && {
          refinementType: refinementType,
          refinementValue: refinementValue
        })
      }
    }
  };

  console.log('Adobe pagination event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a "Show Results" page-size drop-down selection click-action event
 * to the Adobe data layer.
 */
export function pushPageSizeEvent(
  pageSize: number,
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[]
) {
  ensureAdobeDataLayer();

  const hasRefinements = refinementType.length > 0 && refinementValue.length > 0;

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: `Show Results:${pageSize}`,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'Pagination',
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body',
        term: term,
        instances: 1,
        nullInstances: numberOfResults === 0 ? 1 : 0,
        numberOfResults: numberOfResults,
        searchType: searchType,
        refinementInstances: 1,
        ...(hasRefinements && {
          refinementType: refinementType,
          refinementValue: refinementValue
        })
      }
    }
  };

  console.log('Adobe page size event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes a Delete (individual recent search) or Delete All click-action event
 * to the Adobe data layer.
 *
 * @param name  'Delete: <Name of the keyword deleted>' for an individual delete,
 *              or 'Delete All' for the delete-all button.
 */
export function pushDeleteRecentSearchEvent(name: string) {
  ensureAdobeDataLayer();

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: name,
        type: 'other',
        URL: window.location.href
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: 'Icon'
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch: {
        locationInPage: 'body'
      }
    }
  };

  console.log('Adobe delete recent search event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}

/**
 * Pushes an Apply Refiner click-action event to the Adobe data layer.
 * Called after a search triggered by the Apply button in a filter drop-down
 * has returned results. Also reused for Clear Refiner and Clear All Refiner
 * by passing the appropriate options.
 *
 * @param term              The current search term.
 * @param numberOfResults   Total number of results returned.
 * @param searchType        The name of the active vertical (e.g. "All", "Documents").
 * @param refinementType    Array of filter category display names (e.g. ["Service Line"]).
 * @param refinementValue   Array of individual filter values (e.g. ["Assurance", "Consulting"]).
 * @param options           Optional overrides: name, controlType, includeNullInstances.
 */
export function pushApplyRefinerEvent(
  term: string,
  numberOfResults: number,
  searchType: string,
  refinementType: string[],
  refinementValue: string[],
  options?: {
    name?: string;
    controlType?: string;
    includeNullInstances?: boolean;
  }
) {
  ensureAdobeDataLayer();

  const name = options?.name ?? 'Apply Refiner';
  const controlType = options?.controlType ?? 'drop-down';
  const includeNullInstances = options?.includeNullInstances ?? true;

  const supportSiteSearch: Record<string, any> = {
    locationInPage: 'body',
    term: term,
    instances: 1,
    numberOfResults: numberOfResults,
    refinementInstances: 1,
    refinementType: refinementType,
    refinementValue: refinementValue,
    searchType: searchType
  };

  if (includeNullInstances) {
    supportSiteSearch.nullInstances = numberOfResults === 0 ? 1 : 0;
  }

  const obj = {
    event: 'clickAction',
    web: {
      webInteraction: {
        name: name,
        type: 'other'
      }
    },
    _etassureco: {
      controlInteractions: {
        controlType: controlType,
        linkClicks: {
          value: 1
        }
      },
      eyApp: getEyApp(),
      eyPlatform: 'Global Markets',
      eyProduct: 'Discover Reimagined'
    },
    siteKnowledge: {
      supportSiteSearch
    }
  };

  console.log('Adobe refiner event pushed:', obj);
  (window as any)?.adobeDataLayer?.push(obj);
}
